import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import FilmCard from '@/components/feature/FilmCard';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import { films } from '@/mocks/films';
import { GENRES, YEARS, filterFilms, hotFilms, paginate, sortFilms } from '@/lib/film';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [input, setInput] = useState(params.get('q') ?? '');

  const keyword = params.get('q') ?? '';
  const genre = params.get('genre') ?? undefined;
  const yearParam = params.get('year');
  const year = yearParam ? Number(yearParam) : undefined;
  const pageParam = Number(params.get('page') ?? '1');
  const page = Number.isNaN(pageParam) ? 1 : pageParam;

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useSeo({
    title: keyword ? `搜索“${keyword}” - 青青草影视资源平台` : '影视搜索 - 青青草影视资源平台',
    description: keyword
      ? `青青草为你找到与“${keyword}”相关的电影与电视剧作品，支持按类型和年份进一步筛选，快速定位想看的内容。`
      : '在青青草搜索电影、电视剧、导演或演员，快速找到想看的那一部。',
    keywords: `青青草搜索,${keyword || '影视搜索'},影视作品查找,电影搜索,电视剧搜索`,
  });

  const updateParams = (patch: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });
    if (resetPage) next.delete('page');
    setParams(next, { replace: true });
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    updateParams({ q: input.trim() || null });
  };

  useEffect(() => {
    const value = input.trim();
    if (value === keyword) return undefined;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      if (value) next.set('q', value);
      else next.delete('q');
      next.delete('page');
      setParams(next, { replace: true });
    }, 500);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, keyword]);

  const matched = filterFilms(films, { keyword });
  const refined = filterFilms(matched, { genre, year });
  const result = paginate(sortFilms(refined, 'latest'), page, PAGE_SIZE);
  const hasFilter = Boolean(genre || year);
  const suggestions = hotFilms(4).filter((film) => !matched.some((item) => item.id === film.id));

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">搜索结果</span>
          </nav>

          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? (
              <>
                搜索“<span className="text-primary-700">{keyword}</span>”
              </>
            ) : (
              '全站影视搜索'
            )}
          </h1>

          <form onSubmit={submitSearch} className="mt-5 flex w-full max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="flex h-11 flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入片名、导演、演员或类型"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="搜索关键词"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => {
                    setInput('');
                    updateParams({ q: null });
                  }}
                  className="cursor-pointer text-foreground-400 transition-colors hover:text-foreground-700"
                >
                  <i className="ri-close-circle-fill" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span>热门搜索</span>
            {['星海回声', '悬疑', '沈岸', '青春'].map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => updateParams({ q: word })}
                className="cursor-pointer rounded-full bg-background-50 px-2.5 py-1 text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-500">
            {keyword ? (
              <>
                与“<span className="font-semibold text-foreground-900">{keyword}</span>”相关的作品共{' '}
                <span className="font-semibold text-primary-700">{result.total}</span> 部
              </>
            ) : (
              <>
                当前展示全部 <span className="font-semibold text-primary-700">{result.total}</span> 部演示作品
              </>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={genre ?? ''}
              onChange={(event) => updateParams({ genre: event.target.value || null })}
              className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 outline-none focus:border-primary-400"
              aria-label="按类型筛选"
            >
              <option value="">全部类型</option>
              {GENRES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <select
              value={year ? `${year}` : ''}
              onChange={(event) => updateParams({ year: event.target.value || null })}
              className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 outline-none focus:border-primary-400"
              aria-label="按年份筛选"
            >
              <option value="">全部年份</option>
              {YEARS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            {(hasFilter || keyword) && (
              <button
                type="button"
                onClick={() => {
                  setInput('');
                  setParams(new URLSearchParams(), { replace: true });
                }}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-200 px-2.5 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                清空条件
              </button>
            )}
          </div>
        </div>

        <div className="mt-6">
          {result.items.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                {result.items.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination
                  page={result.page}
                  totalPages={result.totalPages}
                  onChange={(nextPage) => updateParams({ page: `${nextPage}` }, false)}
                />
              </div>
            </>
          ) : (
            <EmptyState
              title="没有找到匹配的影视作品"
              description={
                hasFilter
                  ? '关键词有结果，但当前类型或年份筛选把结果过滤掉了，试着放宽筛选条件。'
                  : '试试更换关键词，比如片名中的一个字、导演姓名，或者直接浏览热门推荐。'
              }
              actionLabel={hasFilter ? '清空筛选' : '查看全部作品'}
              onAction={() => {
                if (hasFilter) updateParams({ genre: null, year: null });
                else updateParams({ q: null });
              }}
              secondaryLabel="浏览电影分类"
              secondaryTo="/category?type=movie"
            />
          )}
        </div>

        {result.items.length === 0 && suggestions.length > 0 && (
          <section className="mt-10">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-accent-500" />
              <h2 className="font-heading text-lg font-bold text-foreground-950">你可能会喜欢</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {suggestions.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}